import React, { useState, useRef, useEffect } from 'react';
import { FaInfoCircle } from 'react-icons/fa';

const InfoTooltip = ({ 
  children, 
  infoText, 
  iconSize = 14, 
  iconColor = '#9CA3AF',
  hoverColor = '#8B5CF6',
  position = 'bottom',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let top, left;
      
      // Calculate initial position
      switch (position) {
        case 'top':
          top = buttonRect.top - tooltipRect.height - 8;
          left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          top = buttonRect.bottom + 8;
          left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          top = buttonRect.top + (buttonRect.height / 2) - (tooltipRect.height / 2);
          left = buttonRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          top = buttonRect.top + (buttonRect.height / 2) - (tooltipRect.height / 2);
          left = buttonRect.right + 8;
          break;
        default:
          top = buttonRect.bottom + 8;
          left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);
      }
      
      // Enhanced viewport boundary detection
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const sidebarWidth = viewportWidth * 0.25; // Assuming sidebar is 25% of viewport
      
      // Ensure tooltip stays within viewport and doesn't overlap with main content
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      
      // Special handling for sidebar tooltips - prevent overlap with main content
      if (buttonRect.left < sidebarWidth) {
        // If tooltip is in sidebar, ensure it doesn't extend into main content
        const maxLeftInSidebar = sidebarWidth - tooltipRect.width - 8;
        if (left > maxLeftInSidebar) {
          left = maxLeftInSidebar;
        }
      }
      
      if (top < 8) top = 8;
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = viewportHeight - tooltipRect.height - 8;
      }
      
      // If tooltip would be cut off on the right, try positioning it to the left of the button
      if (left + tooltipRect.width > viewportWidth - 8 && buttonRect.left > tooltipRect.width + 8) {
        left = buttonRect.left - tooltipRect.width - 8;
      }
      
      // If tooltip would be cut off on the bottom, try positioning it above the button
      if (top + tooltipRect.height > viewportHeight - 8 && buttonRect.top > tooltipRect.height + 8) {
        top = buttonRect.top - tooltipRect.height - 8;
      }
      
      setTooltipPosition({ top, left });
    }
  }, [isVisible, position]);

  const handleClick = (e) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={buttonRef}
        onClick={handleClick}
        style={{ 
          color: iconColor, 
          transition: 'all 0.3s',
          transform: 'scale(1)',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          padding: 0
        }}
        onMouseEnter={(e) => {
          e.target.style.color = hoverColor;
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.color = iconColor;
          e.target.style.transform = 'scale(1)';
        }}
      >
        <FaInfoCircle size={iconSize} />
      </button>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: 99,
            background: 'white',
            border: '2px solid #8B5CF6',
            borderRadius: '8px',
            padding: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxWidth: '300px',
            minWidth: '200px',
            fontSize: '0.650rem',
            lineHeight: '1.4',
            color: '#374151',
            fontWeight: 'normal',
            animation: 'fadeIn 0.2s ease-in-out'
          }}
        >
          <div>{children || infoText}</div>
          
          {/* Arrow */}
          <div
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              border: '6px solid transparent',
              ...(position === 'bottom' && {
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderBottomColor: '#8B5CF6'
              }),
              ...(position === 'top' && {
                bottom: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderTopColor: '#8B5CF6'
              }),
              ...(position === 'left' && {
                right: '-12px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderLeftColor: '#8B5CF6'
              }),
              ...(position === 'right' && {
                left: '-12px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderRightColor: '#8B5CF6'
              })
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InfoTooltip; 